import { useState } from 'react';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { providersLabelMap } from 'root/src/shared/constants/providers';
import { isUncountableReward } from 'root/src/client/v2/common/components/RewardRemaining';
import { dareRewardName, isClipsOnlyDare } from 'root/src/client/v2/_pages/drop/redesign/dareCta';
// `estimatedTime` ships in the dare payload (dropEventResponseSchema declares it next to `hint`) but
// is missing from the DropEventDare type. Read it through a narrow local view rather than widening a
// type the legacy page also depends on.
const dareEstimatedTime = (dare) => (dare.estimatedTime);
const DetailRow = ({ label, value }) => (<div className="flex gap-2">
		<dt className="w-20 shrink-0 text-muted-foreground/60">{label}</dt>
		<dd className="flex-1 text-muted-foreground">{value}</dd>
	</div>);
// Everything the card and the row have to cut: the title and description are clamped, the platforms
// are icon-only, and the hint and estimated time are not on either of them at all (the legacy card
// swapped its body for them on hover, which the redesign never carried over).
const dareDetails = (dare) => {
    var _a, _b, _c, _d;
    const streamOn = ((_a = dare.platforms) !== null && _a !== void 0 ? _a : [])
        .map(platform => { var _a; return (_a = providersLabelMap[platform]) !== null && _a !== void 0 ? _a : platform; })
        .join(', ');
    const games = ((_b = dare.games) !== null && _b !== void 0 ? _b : []).map(game => game === null || game === void 0 ? void 0 : game.label).filter(Boolean).join(', ');
    const bundleRewards = ((_c = dare.product) === null || _c === void 0 ? void 0 : _c.type) === 'bundle' ? dare.product.rewards : undefined;
    // Mirrors RewardRemaining: `available` is ALREADY the remaining count, so the stocked pool is
    // available + used. Uncountable rewards (assets / in-game) carry a meaningless sentinel capacity.
    const isUnlimited = isUncountableReward((_d = dare.product) === null || _d === void 0 ? void 0 : _d.type);
    const remaining = isUnlimited
        ? 'Unlimited'
        : dare.capacity
            ? `${Math.max(0, dare.capacity.available)} of ${Math.max(0, dare.capacity.available + dare.capacity.used)} left`
            : undefined;
    return {
        streamOn,
        games,
        bundleRewards,
        remaining,
        estimatedTime: dareEstimatedTime(dare),
        rewardName: dareRewardName(dare),
        clipsOnly: isClipsOnlyDare(dare),
    };
};
// False only for a dare whose payload holds nothing beyond its title (coming-soon placeholders, whose
// fields the serializer masks out), so the panel never opens just to echo the card.
export const dareHasDetails = (dare) => {
    var _a;
    const { streamOn, games, remaining, estimatedTime, clipsOnly } = dareDetails(dare);
    return Boolean(dare.description || dare.hint || estimatedTime || streamOn || games || remaining || clipsOnly || ((_a = dare.product) === null || _a === void 0 ? void 0 : _a.name));
};
// The hover panel shared by the grid card and the list row. Content only: the caller owns the
// Tooltip that positions it, since the card and the row anchor it differently.
export const DareDetailsPanel = ({ dare }) => {
    const { streamOn, games, bundleRewards, remaining, estimatedTime, rewardName, clipsOnly } = dareDetails(dare);
    return (<>
			<p className="font-display text-sm font-semibold text-foreground">{dare.title}</p>
			{dare.description && (<p className="mt-1.5 whitespace-pre-line text-xs leading-relaxed text-muted-foreground">{dare.description}</p>)}

			<dl className="mt-2.5 space-y-1.5 border-t border-solid border-white/[0.06] pt-2.5 text-[11px]">
				{streamOn && <DetailRow label="Stream on" value={streamOn}/>}
				{games && <DetailRow label="Game" value={games}/>}
				{estimatedTime && <DetailRow label="Est. time" value={estimatedTime}/>}
				<DetailRow label="Reward" value={rewardName}/>
				<DetailRow label="You get" value={dare.isRewardScalable
            ? `${dare.amount} (scales with your average CCV/Views)`
            : dare.amount}/>
				{remaining && <DetailRow label="Remaining" value={remaining}/>}
			</dl>

			{bundleRewards && bundleRewards.length > 0 && (<div className="mt-2.5 border-t border-solid border-white/[0.06] pt-2.5">
					<p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Bundle includes</p>
					<ul className="mt-1 flex flex-col gap-1">
						{bundleRewards.map(reward => (<li key={reward.id} className="text-[11px] text-foreground/90">
								<span className="font-semibold text-foreground">({reward.amount})</span> {reward.name}
							</li>))}
					</ul>
				</div>)}

			{clipsOnly && (<p className="mt-2.5 flex gap-1.5 rounded-md bg-white/[0.04] p-2 text-[11px] leading-relaxed text-muted-foreground">
					<ContentCutIcon sx={{ fontSize: 13 }} className="mt-px shrink-0 text-neon-amber"/>
					Twitch deliveries for this dare have to be a clip. A VOD link will be rejected.
				</p>)}

			{dare.hint && (<div className="mt-2.5 rounded-md bg-white/[0.04] p-2">
					<p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-neon-cyan">
						<LightbulbOutlinedIcon sx={{ fontSize: 13 }} className="shrink-0"/>
						Game hint
					</p>
					<p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{dare.hint}</p>
				</div>)}
		</>);
};
// Hover state for a card/row that opens the panel. The panel stands down while one of the nested
// chip tooltips (reward name, amount, clips-only) is up, so the two never stack on one hover. A
// counter, not a boolean: adjacent triggers fire the new one's open before the old one's close, and a
// boolean would flicker the panel back on in between.
export const useDareHoverDetails = (dare) => {
    const [hovering, setHovering] = useState(false);
    const [openHints, setOpenHints] = useState(0);
    return {
        showDetails: hovering && openHints === 0 && dareHasDetails(dare),
        onHintOpenChange: (open) => setOpenHints(count => Math.max(0, count + (open ? 1 : -1))),
        hoverProps: {
            onMouseEnter: () => setHovering(true),
            onMouseLeave: () => setHovering(false),
            // A click on the card opens a dialog (accept / deliver), and tooltips sit above dialog
            // content (z-1102 vs z-1101), so drop the panel on press instead of racing the overlay.
            onPointerDown: () => setHovering(false),
        },
    };
};
